/* eslint-disable */
const {KitModule, registerModule} = window;

let swiperRuntime;

// All approaching carousels share the same import; a failed network request
// can be retried on a later viewport entry without leaving a rejected promise.
function loadSwiper() {
  if (!swiperRuntime) {
    swiperRuntime = import('./swiperRuntime.util.js').catch(error => {
      swiperRuntime = null;
      throw error;
    });
  }
  return swiperRuntime;
}

class Carousel extends KitModule {
  constructor($element) {
    super($element, {observer: {rootMargin: '300px'}});
    this.settings = this.$element.dataset.settings ? JSON.parse(this.$element.dataset.settings) : {};

    this.params = {
      slidesPerView: 1,
      spaceBetween: 16,
      breakpoints: {
        600: { slidesPerView: 1 },
        960: { slidesPerView: 1 },
        1280: { slidesPerView: 2 },
        1440: { slidesPerView: 2 }
      },
      grabCursor: true,
      pagination: {
        clickable: true,
        el: '.pagination__list',
        type: 'fraction'
      },
      navigation: {
        nextEl: '.next',
        prevEl: '.previous'
      }
    };

    this.initializing = false;
    this.prepareLayout();
  }

  // Keep the first frame's slide widths and height stable while the vendor
  // chunk is deferred. Use the same active breakpoint and gap as Swiper.
  prepareLayout() {
    const options = {...this.params, ...this.settings};
    const breakpoint = Object.keys(options.breakpoints || {}).map(Number)
      .filter(width => width <= window.innerWidth).sort((a, b) => b - a)[0];
    const active = {...options, ...(options.breakpoints?.[breakpoint] || {})};
    const slidesPerView = Number(active.slidesPerView);
    if (!Number.isFinite(slidesPerView) || slidesPerView <= 0) {
      this.$element.classList.add('is-prepared');
      return;
    }
    const style = getComputedStyle(this.$element);
    const width = this.$element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const gap = Number(active.spaceBetween) || 0;
    const slideWidth = (width - (slidesPerView - 1) * gap) / slidesPerView;
    this.$element.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').forEach(slide => {
      slide.style.width = `${slideWidth}px`;
      slide.style.marginRight = `${gap}px`;
    });
    this.$element.classList.add('is-prepared');
  }

  onViewportResize() {
    if (!this.swiper) this.prepareLayout();
  }

  async onViewportEnter() {
    if (this.swiper || this.initializing) return;
    this.initializing = true;
    try {
      const {Swiper, A11y, Navigation, Pagination, EffectCreative, EffectFade} = await loadSwiper();
      this.params.modules = [A11y, Navigation, Pagination, EffectCreative, EffectFade];
      const swiperOptions = { ...this.params, ...this.settings };
      const selectedSlide = Number(this.$element.dataset.initialSlide);
      if (Number.isInteger(selectedSlide) && selectedSlide >= 0) {
        swiperOptions.initialSlide = selectedSlide;
      }

      // Swiper pagination.el doesn't support comma-separated selectors.
      // Split into primary (for Swiper) and secondary (synced manually).
      const secondaryPaginationEls = this._extractSecondaryPagination(swiperOptions);

      this.swiper = new Swiper(this.$element, swiperOptions);

      if (secondaryPaginationEls.length) {
        this._syncPagination(secondaryPaginationEls);
      }

      if (this.$element.classList.contains('is-parallax')) {
        import('../../../static/js/lib/parallax-fix').then(module => {
          const ParallaxFix = module.default;
          new ParallaxFix(this.$element).initialize();
        });
      }
    } catch (_) {
      // Keep the prepared slides visible if the chunk fails. The observer can
      // retry when the visitor approaches this carousel again.
    } finally {
      this.initializing = false;
    }
  }

  /**
   * If pagination.el contains comma-separated selectors,
   * keep the first for Swiper and return the rest as DOM elements.
   */
  _extractSecondaryPagination(options) {
    const paginationEl = options.pagination?.el;
    if (typeof paginationEl !== 'string' || !paginationEl.includes(',')) return [];

    const selectors = paginationEl.split(',').map(s => s.trim());
    options.pagination.el = selectors[0];

    return selectors.slice(1)
      .map(sel => document.querySelector(sel))
      .filter(Boolean);
  }

  /**
   * Copy primary pagination innerHTML to secondary elements on slide change.
   */
  _syncPagination(secondaryEls) {
    const sync = () => {
      const primaryEl = this.swiper.pagination.el;
      if (!primaryEl) return;
      secondaryEls.forEach(el => { el.innerHTML = primaryEl.innerHTML; });
    };

    this.swiper.on('slideChange', sync);
    sync(); // Initial sync
  }
}

registerModule('Carousel', Carousel);
